// src/com/auto_dida_info/AutoDidaInfo.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { IconButton, CircularProgress } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
// import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CloudUploadIcon from '@mui/icons-material/CloudUpload'; // 업로드
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import SearchBox from '../../common/SearchBox';
import {
    ContentConainer, ContentHeader, ContentBody, ControlBox, ToolBox
} from '../../common/TopViewControl';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import {
    authTableTreeRepo, autoDidaInfoListRepo, autoDidaInfoCountsRepo
} from '../../value/AppModel';
import {
    LIST_AUTO_DIDA_INFO, LIST_AUTO_DIDA_INFO_COUNTS, GET_AUTO_DIDA_INFO,
    ADD_AUTO_DIDA_INFO, EDIT_AUTO_DIDA_INFO, REMOVE_AUTO_DIDA_INFO
} from './AutoDidaInfoGql';
import { AutoDidaInfoDictionary } from './AutoDidaInfoDict';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import MonthPicker from '../../common/MonthPicker';
import FlexyTable from '../../common/flexytable/FlexyTable';
import AutoDidaInfoInput from './AutoDidaInfoInput';
import ValueMisc from '../../value/ValueMisc';
import AutoDidaCountTree from './AutoDidaCountTree';
import AutoDidaInfoUploader from './AutoDidaInfoUploader';
import InputUtil from '../../value/InputUtil';

const ColumnContainer = styled(Box)({
    display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
});

/**
 * 점포휴무일 관리 View Controller
 * 자동배차 > 상온 자동배차 기초 정보 > 점포 휴무일 등록
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-10-18 15:31:59 using DbdbTap
 */
export default function AutoDidaInfo({
    viewServiceTags, // for switching view
    show = true,
}) {
    const [dcSelected, setDcSelected] = useState({});
    // const dcSelected = useReactiveVar(autoDidaDcRepo);
    const [monthSelected, setMonthSelected] = useState(null);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const [editorState, setEditorState] = useState(AppObject.EmptyViewerState);
    const [uploadViewState, setUploadViewState] = useState({open:false, csvText:''});
    const autoDidainfoCountsData = useReactiveVar(autoDidaInfoCountsRepo);
    const autoDidaInfoListData = useReactiveVar(autoDidaInfoListRepo);
    const [searchText, setSearchText] = useState('');
    const openConfirmDialog = useConfirmDialog();
    const [useEucKr, setUseEucKr] = useState(false);
    const checkAuth = useAuthorizationCheck();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const fileInputRef = useRef(null);
    const lastReloadRef = useRef(0);

    // ##### Call GraphQL to get List #####
    const [getAutoDidaInfoCounts, responseCounts] = useLazyQuery(LIST_AUTO_DIDA_INFO_COUNTS, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetCounts(data, option)},
        onError: (error) => addAlert({title: '휴무일 현황 조회 오류', message: [error.message]})
    });

    const [getAutoDidaInfoList, responseList] = useLazyQuery(LIST_AUTO_DIDA_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: AutoDidaInfoDictionary.errorTitle.Get, message: [error.message]})
    });
    const [getAutoDidaInfoItem, responseItem] = useLazyQuery(GET_AUTO_DIDA_INFO, {
		...AppObject.NoCachedFetch,
		onCompleted: (data, option) => {onCompleteGetItem(data, option)},
		onError: (error) => addAlert({title: AutoDidaInfoDictionary.errorTitle.Get, message: [error.message]})
	});
    
    // ##### GraphQL Mutation. 'onError' for prevent from printing error at console (Uncaught exception) ###
    const [addAutoDidaInfo, responseAdd] = useMutation( ADD_AUTO_DIDA_INFO, {
		onCompleted: (data, option) => onCompleteAdd(data, option), 
		onError: (error) => addAlert({title: AutoDidaInfoDictionary.errorTitle.Add, message: [error.message]})
	} );
    const [editAutoDidaInfo, responseEdit] = useMutation( EDIT_AUTO_DIDA_INFO, {
		onCompleted: (data, option) => onCompleteEdit(data, option),
		onError: (error) => addAlert({title: AutoDidaInfoDictionary.errorTitle.Edit, message: [error.message]})
	} );
    const [removeAutoDidaInfo, responseRemove] = useMutation( REMOVE_AUTO_DIDA_INFO, {
		onCompleted: (data, option) => onCompleteRemove(data, option),
		onError: (error) => addAlert({title: AutoDidaInfoDictionary.errorTitle.Remove, message: [error.message]})
	});

    function getAutoDidaInfoListNow(dcCd, dateFrom) {
        const param = {variables:{dcCd, dateFrom}};
        getAutoDidaInfoList(param);
        lastReloadRef.current = new Date().getTime()/1000;
    };

    function getAutoDidaInfoCountsNow(dateFrom) {
        getAutoDidaInfoCounts({variables:{dateFrom}});
    }

    useEffect(()=>{
        const firstDate = ValueMisc.getFirtDateOfMonth();
        const ymd = ValueMisc.getYMD(firstDate);
        setMonthSelected(ymd);
        getAutoDidaInfoCountsNow(ymd);
    }, []);

    useEffect(()=>{
        setDcSelected(autoDidaInfoListData.dc);
    }, [autoDidaInfoListData]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetCounts = (data, clientOption) => {
        if(data.autoDidaInfoCounts) {
            autoDidaInfoCountsRepo({when:new Date().getTime()/1000, records:data.autoDidaInfoCounts});
        }
    };

    const onCompleteGetList = (data, clientOption) => {
        if(data.autoDidaInfoList) {
            const dc = data.autoDidaInfoList.length > 0 ?
                {dcCd:data.autoDidaInfoList[0].dcCd, dcName:data.autoDidaInfoList[0].dcName} :
                dcSelected;
            autoDidaInfoListRepo({
                when:new Date().getTime()/1000,
                dc,
                records:data.autoDidaInfoList.map((item)=>{
                    item.dcCd_storeId_didaDate = `${item.dcCd}_${item.storeId}_${item.didaDate}`;
                    return item;
                })
            });
        }
    };

    const onCompleteGetItem = (data, option) => {
        if(data.autoDidaInfoItem) {
            setEditorState({open:true, item:data.autoDidaInfoItem});
        }
        else addAlert({title:AutoDidaInfoDictionary.errorTitle.Get, message:['점포 휴무일 정보를 찾을 수 없습니다.']});
    };

    const onCompleteAdd = (data, clientOption) => {
        if(data.autoDidaInfoAdd.ok) {
            setEditorState(AppObject.EmptyViewerState);
            getAutoDidaInfoListNow(dcSelected.dcCd, monthSelected);
        }
        else addAlert({title:AutoDidaInfoDictionary.errorTitle.Add, message:[data.autoDidaInfoAdd.message]});
    };

    const onCompleteEdit = (data, clientOption) => {
        if(data.autoDidaInfoEdit.ok) {
            setEditorState(AppObject.EmptyViewerState);
            getAutoDidaInfoListNow(dcSelected.dcCd, monthSelected);
        }
        else addAlert({title:AutoDidaInfoDictionary.errorTitle.Edit, message:[data.autoDidaInfoEdit.message]});
    };

    const onCompleteRemove = (data, clientOption) => {
        if(data.autoDidaInfoRemove.ok) getAutoDidaInfoListNow(dcSelected.dcCd, monthSelected);
        else addAlert({title:AutoDidaInfoDictionary.errorTitle.Remove, message:[data.autoDidaInfoRemove.message]});
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 3) {
            // Too short to reload
            enqueueSnackbar(AppWord.ReloadNotIn3Sec, {variant:'info', autoHideDuration:2000});
        }
        else if(dcSelected.dcCd) getAutoDidaInfoListNow(dcSelected.dcCd, monthSelected);
    };

    const onRequestSave = (item, isEdit) => {
        const mutateParam = {variables:{autoDidaInfo:item}};
        if(isEdit) editAutoDidaInfo(mutateParam);
        else addAutoDidaInfo(mutateParam);
    };

    const onClickAdd = () => {
        setEditorState({open:true, item:null});
    };

    const onRequestEdit = (item) => {
        getAutoDidaInfoItem({variables:{dcCd:item.dcCd, storeId:item.storeId, didaDate:item.didaDate}});
    };

    const confirmRemove = async (item) => {
        const confirm = await openConfirmDialog({
            title: "점포 휴무일 삭제",
            content: [
                `점포 휴무일을 삭제하시겠습니까?`,
                '삭제 후 복구할 수 없습니다. 계속하시겠습니까?'
            ]
        });

        if(confirm)
            removeAutoDidaInfo({variables:{autoDidaInfo:{dcCd:item.dcCd, storeId:item.storeId, didaDate:item.didaDate}}});
    };

    // 월 선택시 DC*요일별 휴무일 카운트 조회
    const onChangeMonth = (id, value) => {
        setMonthSelected(value);
        getAutoDidaInfoCountsNow(value);
        autoDidaInfoListRepo({when:0, records:[], dc:{}});
    };

    // DC 클릭하면 해당 DC*월의 휴무일 정보 조회
    const onClickDcOnTree = (dc) => {
        setDcSelected(dc);
        getAutoDidaInfoListNow(dc.dcCd, monthSelected);
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.DELETE: confirmRemove(row); break;
            case AppObject.command.EDIT: onRequestEdit(row); break;
            default: return;
        }
    };

    const onCsvFileRead = (content, isEucKr) => {
        setUseEucKr(isEucKr);
        setUploadViewState({open:true, csvText:content});
    };

    const onChooseFile = (event) => {
        if(event.target.files.length > 0) {
            const file = event.target.files[0];
    
            if (file) {
                InputUtil.readTextFileWithAutoEncoding(file, onCsvFileRead);
            }
        }
        // 선택 완료 후 초기화
        if(fileInputRef.current) fileInputRef.current.value = '';
    };

    const onClickUpload = () => {
        // DC 선택 여부 상관없이 파일 업로드 창 오픈
        if(fileInputRef.current) {
            fileInputRef.current.value='';
            fileInputRef.current.click();
        }
    };

    const onCloseUploader = (countGood) => {
        setUploadViewState({open:false, csvText:''});
        if(countGood>0){
            // 1. 좌측 트리의 센터별 휴무일 카운트 전체 갱신
            if(monthSelected) getAutoDidaInfoCountsNow(monthSelected);

            // 2. 현재 DC가 선택되어 있다면 해당 DC의 리스트 갱신
            if(dcSelected && dcSelected.dcCd) {
                getAutoDidaInfoListNow(dcSelected.dcCd, monthSelected);
            }
        }
    };

    /*
	CALL wps_set_auth_table('pr_auto_dida_info_add', '1,2', v_msg);
	CALL wps_set_auth_table('pr_auto_dida_info_edit', '1,2', v_msg);
	CALL wps_set_auth_table('pr_auto_dida_info_remove', '1,2', v_msg);
    */

    const canAdd = checkAuth(authTableTree.tree.pr_auto_dida_info_add);
    const canEdit = checkAuth(authTableTree.tree.pr_auto_dida_info_edit);
    const canRemove = checkAuth(authTableTree.tree.pr_auto_dida_info_remove);
    const tools = []; /* canEdit ? [
        {id:AppObject.command.EDIT, icon:<EditIcon/>},
        {id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning},
    ] : []; */

    if(canEdit) tools.push({id:AppObject.command.EDIT, icon:<EditIcon/>});
    if(canRemove) tools.push({id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning});

    const loadingAny
    = responseList.loading
    || responseCounts.loading
    || responseEdit.loading
    || responseAdd.loading
    || responseRemove.loading
    || responseItem.loading;

    /* const renderUploadPop = () => {
        return(
            <Popover
                open={Boolean(uploadPopAnchorEl)}
                anchorEl={uploadPopAnchorEl}
                onClose={()=>setUploadPopAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Box style={{padding:10, maxWidth:300}}>
                    <Box style={{
                        marginBottom:10, padding:10, borderBottom:AppStyle.BorderCCC, borderRadius:5,
                        backgroundColor: AppStyle.PrimaryRGB, color:'white'
                    }}>
                        파일 업로드하기
                    </Box>
                    <p style={{fontSize:'0.9rem'}}>
                        점포 휴무일 일괄 등록을 위해 CSV 파일을 준비해 주세요.<br/>
                        파일이 CSV 파일로 저장될 때 일반적으로는 UTF-8 인코딩이 사용됩니다.
                        하지만, 오래전에 작성된 파일이나 저장한 프로그램에 따라 EUC-KR 인코딩(완성형)이
                        사용된 경우도 있습니다. 이 경우 아래 체크박스를 선택하세요.<br/>
                        잘 모르시면, 일단 열어본 후 한글이 깨지면 체크 상태를 바꾸고
                        다시 열어보세요.
                    </p>
                    <Box style={{borderTop:'1px solid #ccc'}}>
                        <VzmCheckbox
                            dict={{
                                id: 'useEucKr', label: '업로드 파일 완성형한글로 읽기',
                            }}
                            value={useEucKr}
                            onChange={(id,value,err) => setUseEucKr(value)}
                        />
                    </Box>
                    <Button
                        fullWidth
                        variant={AppStyle.mui.contained}
                        onClick={onClickUpload}
                        startIcon={<CloudUploadIcon/>}
                    >
                        점포 휴무일 일괄 등록
                    </Button>
                </Box>
            </Popover>
        );
    }; */

    const rootStyle = show ? undefined : {display:'none', visibility:'hidden', pointerEvents:'none'};

    // 좌측 트리에 표시된 DC 목록에서 dcCd 추출
    const validDcList = (autoDidainfoCountsData?.records || []).map(item => item.dcCd);

    return(
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
            <ContentConainer style={rootStyle}>
                {viewServiceTags}
                <ContentBody
                    style={{display:'flex'}}
                >
                    <ColumnContainer width={240} style={{paddingRight:10}}>
                        <ContentHeader>
                            <ControlBox>
                                <MonthPicker
                                    dict={{id:'dateFrom', label:'월 선택'}}
                                    value={monthSelected}
                                    monthsAgo={30}
                                    upToAfter={6}
                                    onChange={onChangeMonth}
                                />
                            </ControlBox>
                        </ContentHeader>
                        <Box style={{
                                flexGrow:1, position:'relative',
                                border: AppStyle.BorderCCC, boxSizing:'border-box',
                                borderRadius:5, padding:10,
                        }}>
                            {
                                autoDidainfoCountsData && autoDidainfoCountsData.records && autoDidainfoCountsData.records.length > 0 ? (
                                <AutoDidaCountTree
                                    list={autoDidainfoCountsData.records}
                                    onClickDc={onClickDcOnTree}
                                />
                                ) : (
                                    <Box style={{
                                        display: 'flex', justifyContent: 'center', alignItems: 'center',
                                        height: '100%', minHeight: 200, color: AppStyle.Gray600, fontSize: '0.9rem'
                                    }}>
                                        조회된 데이터가 없습니다.
                                    </Box>
                                )
                            }
                        </Box>
                    </ColumnContainer>
                    <ColumnContainer flexGrow={1}>
                        <ContentHeader>
                            <ControlBox>
                                <Box style={{
                                    fontWeight:'bold', fontSize:'1.05rem',
                                    marginRight:50, display:'flex', alignItems:'center',
                                }}>
                                {
                                    dcSelected && dcSelected.dcCd ?
                                    <span>
                                        {dcSelected.dcName} ({dcSelected.dcCd})
                                    </span>
                                    :
                                    <span style={{color:AppStyle.Gray600}}>
                                        좌측에서 DC를 선택하세요.
                                    </span>
                                }
                                </Box>
                                <SearchBox
                                    value={searchText}
                                    onChangeText={onChangeSearchText}
                                    disabled={responseList.loading || autoDidaInfoListData.records.length < 2}
                                />
                            </ControlBox>
                            <ToolBox>
                                {
                                    loadingAny ?
                                    <CircularProgress size={20} />
                                    :
                                    <IconButton
                                        color={AppStyle.mui.inherit}
                                        onClick={onClickReload}
                                        disabled={!dcSelected.dcCd || !monthSelected}
                                    >
                                        <CachedIcon />
                                    </IconButton>
                                }
                                {
                                    canAdd &&
                                    <IconButton
                                        color={AppStyle.mui.primary}
                                        style={{marginLeft:10}}
                                        onClick={onClickAdd}
                                        disabled={responseEdit.loading || responseAdd.loading || !dcSelected.dcCd}
                                    >
                                        <NoteAddIcon />
                                    </IconButton>
                                }
                                <input
                                    type="file"
                                    accept=".csv"
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                    onChange={onChooseFile}
                                />
                                {
                                    // fileInputRef.current && canAdd &&
                                    canAdd &&
                                    <IconButton
                                        color={AppStyle.mui.standard}
                                        onClick={onClickUpload}
                                        title='점포 휴무일 일괄 등록'
                                        style={{marginLeft:10}}
                                        //disabled={responseEdit.loading || responseAdd.loading || !dcSelected.dcCd}
                                        disabled={responseEdit.loading || responseAdd.loading}
                                    >
                                        <CloudUploadIcon />
                                    </IconButton>
                                }
                            </ToolBox>
                        </ContentHeader>
                        <FlexyTable
                            name={AutoDidaInfoDictionary.tableProps.tableId}
                            tableProps={AutoDidaInfoDictionary.tableProps}
                            records={autoDidaInfoListData.records}
                            search={searchText}
                            tools={tools}
                            onClickTool={onClickToolOnRow}
                        />
                    </ColumnContainer>
                </ContentBody>
                <AutoDidaInfoInput
                    open={editorState.open}
                    dc={dcSelected}
                    item={editorState.item}
                    onClose={()=>setEditorState(AppObject.EmptyViewerState)}
                    responseSaving={editorState.item ? responseEdit : responseAdd}
                    onRequestSave={onRequestSave}
                />
                <AutoDidaInfoUploader
                    open={uploadViewState.open}
                    csvText={uploadViewState.csvText}
                    isEucKr={useEucKr}
                    onClose={onCloseUploader}
                    dc={dcSelected}
                    validDcList={validDcList}
                />
            </ContentConainer>
        </LocalizationProvider>
    );
}
